// ---------------------------------------------------------------------------
// management-view-quality-control-api
//
// Frontend service wrapper for the bulk Quality Control matrices endpoint
// (US2 / PRODOPSAI-395). One round-trip per filter change returns every
// governance-group matrix the QualityControlTab renders.
//
// Per CLAUDE.md, the `api` wrapper prepends `/api` to every endpoint, so the
// strings passed in must NOT include the `/api` prefix.
// ---------------------------------------------------------------------------

import { api } from '@/lib/api';
import { buildManagementViewParams, type IManagementViewQuery } from '@/lib/management-view-query';
import type {
  IApiResponse,
  IQualityControlMatricesResponse,
} from '@skystone/shared';

export type IQualityControlQuery = IManagementViewQuery;

export const managementViewQualityControlApi = {
  /**
   * `GET /api/management-view/quality-control` — returns every governance
   * matrix (Phase 1: Status Count, Issue-Type Compliance, Epic Linkage) in
   * a single envelope ordered by registry `displayOrder`. Used by
   * `QualityControlTab` to render the three `MatrixCard` instances.
   *
   * `signal` allows callers (e.g. `useQualityControlMatrices`) to abort an
   * in-flight request when the user changes filters quickly. The wrapper
   * passes it through to `fetch` via the underlying `api.get` call.
   */
  getMatrices: async (
    query: IQualityControlQuery,
  ): Promise<IQualityControlMatricesResponse> => {
    const params = buildManagementViewParams(query);
    const result = await api.get<IApiResponse<IQualityControlMatricesResponse>>(
      `/management-view/quality-control?${params.toString()}`,
    );
    return result.data;
  },
};
